import {
  TRIP_DEFAULT_TRAVEL_MODES,
  type TripDefaultTravelMode,
} from "@poi-planner/shared";
import { useTripStore } from "../store/tripStore";
import { TRAVEL_MODE_LABELS, tripTravelModePreferences } from "../utils/geo";
import { FormSelect } from "./FormFields";

const KM_TO_MILES = 0.621371;
const WALK_THRESHOLD_CHOICES_KM = [0, 0.5, 0.8, 1, 1.5, 2, 3];

function walkThresholdLabel(km: number): string {
  if (km === 0) return "Never walk automatically";
  const miles = km * KM_TO_MILES;
  return `Up to ${km} km (${miles.toFixed(1)} mi)`;
}

/**
 * Trip-wide defaults for the calendar's automatic travel pins: walk hops up
 * to a distance, otherwise the default mode. Individual pins can still be
 * overridden from their hover card.
 */
export function TripTravelSettings({ readOnly }: { readOnly: boolean }) {
  const trip = useTripStore((state) => state.trip);
  const setTripTravelDefaults = useTripStore(
    (state) => state.setTripTravelDefaults,
  );
  if (!trip) return null;

  const { defaultTravelMode, walkThresholdKm } =
    tripTravelModePreferences(trip);
  const walkChoices = WALK_THRESHOLD_CHOICES_KM.includes(walkThresholdKm)
    ? WALK_THRESHOLD_CHOICES_KM
    : [...WALK_THRESHOLD_CHOICES_KM, walkThresholdKm].sort((a, b) => a - b);

  return (
    <section aria-labelledby="trip-travel-heading">
      <div className="mb-2">
        <h4
          id="trip-travel-heading"
          className="m-0 text-xs font-extrabold uppercase tracking-wider text-secondary"
        >
          Travel between stops
        </h4>
        <p className="mb-0 mt-1 text-xs font-medium text-tertiary">
          Sets the automatic mode for calendar travel pins. A pin set to a
          specific mode keeps it.
        </p>
      </div>
      <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-bold text-secondary">Default mode</span>
          <FormSelect
            data-testid="trip-default-travel-mode"
            aria-label="Default travel mode"
            value={defaultTravelMode}
            disabled={readOnly}
            onChange={(value) =>
              setTripTravelDefaults({
                defaultTravelMode: value as TripDefaultTravelMode,
              })
            }
            options={TRIP_DEFAULT_TRAVEL_MODES.map((mode) => ({
              value: mode,
              label: TRAVEL_MODE_LABELS[mode],
            }))}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-bold text-secondary">
            Walk short hops
          </span>
          <FormSelect
            data-testid="trip-walk-threshold"
            aria-label="Walk short hops"
            value={String(walkThresholdKm)}
            disabled={readOnly}
            onChange={(value) =>
              setTripTravelDefaults({ walkThresholdKm: Number(value) })
            }
            options={walkChoices.map((km) => ({
              value: String(km),
              label: walkThresholdLabel(km),
            }))}
          />
        </label>
      </div>
    </section>
  );
}
